import { numberValidationRules } from './number-validation-rules';
import { dateValidationRules } from './date-validation-rules';
import { textValidationRules } from './text-validation-rules';
import { timeValidationRules } from './time-validation-rules';
import { ValidationResult } from '../interfaces/validation-result';
import { BasicValidationRules } from '../interfaces/basic-validation-rules';
import { ValidationRules } from '../interfaces/validation-rules';
import { Validator } from '../interfaces/validator';
import { StringUtil } from '../utils/string-util';
import { i18n, I18nUtil } from '../utils/i18n-util';
import {Time} from '../interfaces/time';
import {SimpleTime} from '../models/simple-time';

//by using a singleton here, we can avoid creating a brand new object every time.
let validResult = {
  isValid: true
};

//The point of this function is to not create a property on the BasicValidationRules object
//when the value is undefined, null, or ''.
//This is private. (really)
//This gets connected to an empty ValidationRules object
let setProperty = function(property: string, value: string, type: any = String): void {
  let parsedValue;
  //Empty values are ignored.
  if (value == null || value === '') {
    return;
  }

  if (type === Boolean) {
    // this is here because new Boolean('false') evaluates to true
    parsedValue = value === 'true';
  } else if (type === Number) {
    parsedValue = type(value);
  } else {
    parsedValue = new type(value);
  }

  if (typeof parsedValue === 'number' && isNaN(parsedValue)) {
    return;
  }

  if (parsedValue instanceof Date && isNaN(parsedValue.getTime())) {
    return;
  }
  this[property] = parsedValue;
};
/**
 * This is a collection of static functions that will be used to support a single static function.
 *
 * pdsValidationRulesEngine.validate(inputString, rules)
 *
 * example: pdsValidationRulesEngine.validate(12345, {isRequired: true, maxValue: 100, minValue: 0, isInteger: true});
 */
export class ValidationRulesEngine {
  i18n = i18n;
  // this only needs to be called if you aren't using the singleton i18n object provided by this module.
  setI18n(i18nObject): void {
    this.i18n = i18nObject;
    numberValidationRules.i18n = i18nObject;
    dateValidationRules.i18n = i18nObject;
    textValidationRules.i18n = i18nObject;
    timeValidationRules.i18n = i18nObject;
  }

  // * * * * * * Public Functions * * * * * *
  /**
   * This is a separate function because it allows for the validators for the set of rules to be cached.
   * @param rules
   * @private
   */
  getValidatorsForRules(rules: ValidationRules): Array<Validator> {
    let validators: Array<Validator> = [];
    if (rules.isNumber) {
      validators = this._getNumberValidators(rules);
    } else if (rules.isDate) {
      validators = this._getDateValidators(rules);
    } else if (rules.isText) {
      validators = this._getTextValidators(rules);
    } else if (rules.isTime) {
      validators = this._getTimeValidators(rules);
    }
    return validators;
  }

  validate(value: string, validators: Array<Validator>): ValidationResult {
    //If there are no validators, then it's valid!
    if (validators == null || validators.length === 0) {
      return {
        isValid: true
      };
    }

    //We are assuming that if isRequired would be the first validator.
    //If we decide there is a case where something else needs to be run first then
    //we can alter this test.
    if (
      validators[0].execute !== this._validateIsRequired && //not required
      (value === '' || value == null)
    ) {
      //no value
      return {
        isValid: true
      };
    }

    let context = {};
    for (let validator of validators) {
      //NOTE: validator run in the context of the validationRulesEngine
      let result: ValidationResult = validator.execute.call(this, value, validator.condition, context);

      if (result.ignoreSubsequentValidators === true || !result.isValid) {
        return result;
      }
    }

    //If we made it here, it's valid.
    return {
      isValid: true
    };
  }

  validateWithRules(value: string, rules: ValidationRules): ValidationResult {
    let validators = this.getValidatorsForRules(rules);
    //console.log('validators.length: ' + validators.length);
    return this.validate(value, validators);
  }

  getBasicValidationRules(): BasicValidationRules {
    return {
      setProperty: setProperty
    };
  }

  //* * * * * Validator Functions * * * * *
  private _validateIsRequired(input: string, condition: any, context: any): ValidationResult {
    if (input == null || input.trim().length === 0) {
      return {
        isValid: false,
        errorMessage: this.i18n.getText('cu|required'),
        invalidRule: 'isRequired'
      };
    } else {
      return validResult;
    }
  }

  //* * * * * GetValidators functions * * * * * * *
  private _getNumberValidators(rules: ValidationRules): Array<Validator> {
    //console.log('gettign number validators');
    let validators: Array<Validator> = [];
    if (rules.isRequired) {
      validators.push({
        execute: this._validateIsRequired
      });
    }

    //No if here. If it's a number, it needs to be a valid number.
    validators.push({
      execute: numberValidationRules.validateIsNumber
    });

    if (rules.isInteger) {
      validators.push({
        execute: numberValidationRules.validateIsInteger
      });
    }

    if (rules.maxValue != null && typeof rules.maxValue !== 'string') {
      validators.push({
        execute: numberValidationRules.validateMaxNumberValue,
        condition: rules.maxValue
      });
    }

    if (rules.minValue != null && typeof rules.minValue !== 'string') {
      validators.push({
        execute: numberValidationRules.validateMinNumberValue,
        condition: rules.minValue
      });
    }

    if (rules.maxValueExclusive != null && typeof rules.maxValueExclusive !== 'string') {
      validators.push({
        execute: numberValidationRules.validateMaxNumberValueExclusive,
        condition: rules.maxValueExclusive
      });
    }

    if (rules.minValueExclusive != null && typeof rules.minValueExclusive !== 'string') {
      validators.push({
        execute: numberValidationRules.validateMinNumberValueExclusive,
        condition: rules.minValueExclusive
      });
    }

    if (rules.maxLength != null) {
      validators.push({
        execute: numberValidationRules.validateMaxNumberLength,
        condition: rules.maxLength
      });
    }

    if (rules.minLength != null) {
      validators.push({
        execute: numberValidationRules.validateMinNumberLength,
        condition: rules.minLength
      });
    }

    if (rules.maxDecimals != null) {
      validators.push({
        execute: numberValidationRules.validateMaxDecimals,
        condition: rules.maxDecimals
      });
    }

    return validators;
  }

  private _getDateValidators(rules: ValidationRules): Array<Validator> {
    //console.log('getting date validators');
    let validators: Array<Validator> = [];

    if (rules.isRequired) {
      validators.push({
        execute: this._validateIsRequired
      });
    }

    validators.push({
      execute: dateValidationRules.validateIsDate
    });

    if (rules.maxValue instanceof Date) {
      validators.push({
        execute: dateValidationRules.validateMaxDate,
        condition: rules.maxValue
      });
    }

    if (rules.minValue instanceof Date) {
      validators.push({
        execute: dateValidationRules.validateMinDate,
        condition: rules.minValue
      });
    }

    return validators;
  }

  private _getTimeValidators(rules: ValidationRules): Array<Validator> {
    //console.log('getting date validators');
    let validators: Array<Validator> = [];

    if (rules.isRequired) {
      validators.push({
        execute: this._validateIsRequired
      });
    }

    validators.push({
      execute: timeValidationRules.validateIsTime
    });

    if (rules.maxValue != null && typeof (<any>rules.maxValue).getHours === 'function' && typeof (<any>rules.maxValue).getMinutes === 'function') {
      validators.push({
        execute: timeValidationRules.validateMaxTime,
        condition: rules.maxValue
      });
    }

    if (rules.minValue != null && typeof (<any>rules.minValue).getHours === 'function' && typeof (<any>rules.minValue).getMinutes === 'function') {
      validators.push({
        execute: timeValidationRules.validateMinTime,
        condition: rules.minValue
      });
    }

    return validators;
  }

  private _getTextValidators(rules: ValidationRules): Array<Validator> {
    let validators: Array<Validator> = [];
    if (rules.isRequired) {
      validators.push({
        execute: this._validateIsRequired
      });
    }

    if (rules.maxLength != null) {
      validators.push({
        execute: textValidationRules.validateMaxTextLength,
        condition: rules.maxLength
      });
    }

    if (rules.minLength != null) {
      validators.push({
        execute: textValidationRules.validateMinTextLength,
        condition: rules.minLength
      });
    }

    if (rules.excludeChars != null) {
      //Escape some characters that would mess up the regex.
      // let escapedChars =  rules.excludeChars.replace('^', '\\^').replace('\]', '\\]').replace('-', '\\-');
      let escapedChars = rules.excludeChars.replace(StringUtil.ESCAPE_EXCLUDE_CHARS_REGEX, '\\$&');

      validators.push({
        execute: textValidationRules.validateExcludeChars,
        condition: {
          pattern: new RegExp(`[${escapedChars}]`, 'i'),
          excludeChars: rules.excludeChars
        }
      });
    }

    if (rules.regex != null) {
      validators.push({
        execute: textValidationRules.validateRegex,
        condition: rules.regex
      });
    }

    if (rules.inverseRegex != null) {
      validators.push({
        execute: textValidationRules.validateInverseRegex,
        condition: rules.inverseRegex
      });
    }

    if (rules.isEmail) {
      validators.push({
        execute: textValidationRules.validateEmail
      });
    }

    return validators;
  }
}
